// Le bloc « Tes premiers pas » (D-091, maquettes b1, b4, b5, b6) : sur l'accueil de chaque rôle tant
// qu'il reste une étape, cochée TOUTE SEULE d'après les données (onboarding_state) ; il disparaît quand
// tout est fait et reste dans Aide › Mes premiers pas. « Montre-moi » lance la visite de l'étape.
// L'app posée sur l'écran d'accueil se voit ici (affichage « standalone ») et se déclare (guide_mark).
import { guideMark, onboardingState, type OnboardingState, type StepState } from '../lib/guide';
import { activeSeason, ffbbLinkedTeams } from '../lib/api';
import { firstSteps, firstStepsHead, nextStep, stepProgress, STEP_KEYS, type FirstStep, type GuideRole, type StepInfo } from './steps';
import { demoNow, forcedReal } from './demo';
import { children, genderOf, me, personaOf, workTeams, type Ctx } from '../ui/context';
import { agree, displayName } from '../domain/labels';
import { loadAgenda, type AgendaItem } from '../ui/agenda';
import { isRtView } from '../ui/rt-shared';
import { time } from '../ui/format';
import { localDate, parseLocalDate } from '../domain/schedule';
import { html, mount, type Html } from '../ui/dom';

const HOST_BASE = (import.meta.env.VITE_CLUB_HOST_BASE as string | undefined)?.trim() || null;

/** Le rôle des premiers pas de la casquette affichée (aucun pour le supporter, l'éditeur). */
export function guideRoleOf(c: Ctx): GuideRole | null {
  if (isRtView(c)) return 'technique';
  const p = personaOf(c);
  if (p === 'coach') return 'coach';
  if (p === 'bureau' || p === 'joueur' || p === 'parent' || p === 'partenaire') return p;
  return null;
}

let cache: { club: string; at: number; p: Promise<OnboardingState | null> } | null = null;
/** L'état des premiers pas (relu au plus toutes les 20 s, et après chaque visite finie). */
export function loadState(c: Ctx, fresh = false): Promise<OnboardingState | null> {
  const club = c.club?.id;
  if (!club || club.startsWith('host:')) return Promise.resolve(null);
  if (fresh || !cache || cache.club !== club || Date.now() - cache.at > 20_000) {
    cache = { club, at: Date.now(), p: onboardingState(club).catch(() => null) };
  }
  return cache.p;
}
export const forgetState = () => { cache = null; };

/** L'app ouverte depuis son icône : l'étape « écran d'accueil » se coche toute seule. */
let standaloneSent = false;
export function noteStandalone(c: Ctx): void {
  if (standaloneSent || !c.club || c.club.id.startsWith('host:')) return;
  const standalone = matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (!standalone) return;
  standaloneSent = true;
  void guideMark(c.club.id, 'ecran').then(() => forgetState(), () => undefined);
}

const stepsOfRole = (s: OnboardingState | null, role: GuideRole): StepState[] | null => s?.roles.find(r => r.role === role)?.steps ?? null;
/** Les étapes facultatives du rôle (bureau, D-122). */
const optionalOf = (s: OnboardingState | null, role: GuideRole): StepState[] => s?.roles.find(r => r.role === role)?.optional ?? [];

/** De quoi rendre les étapes concrètes : noms, prochaine séance, prochain match. */
async function infoFor(c: Ctx, role: GuideRole, required: StepState[], items?: AgendaItem[], optional: StepState[] = []): Promise<StepInfo> {
  const steps = [...required, ...optional];
  const done = Object.fromEntries(steps.map(s => [s.key, s.done]));
  const n = Object.fromEntries(steps.map(s => [s.key, s.n]));
  const info: StepInfo = { done, n, slug: steps.find(s => s.key === 'page')?.slug ?? null, hostBase: HOST_BASE, clubName: c.club?.name,
    optional: optional.map(s => s.key) };
  const team = workTeams(c)[0];
  if (team) { info.teamName = team.name; info.gender = team.gender; }
  const self = me(c);
  if (self) { info.personName = displayName(self); info.gender = genderOf(c, self.id); }
  const kid = children(c)[0];
  if (kid) info.kidName = displayName(kid);
  if (role === 'partenaire') info.partnerName = c.partners[0]?.name;
  // Reliées à la FFBB ou non : le texte de « Le club est créé » / « Ton équipe est prête » dit ce qui est vrai.
  if (role === 'bureau' && c.club) {
    const clubId = c.club.id;
    const season = await activeSeason(clubId).catch(() => null);
    const ids = c.full.teams.filter(t => t.club_id === clubId && (!season || t.season_id === season.id)).map(t => t.id);
    const linked = ids.length ? await ffbbLinkedTeams(ids).catch(() => null) : [];
    if (linked) info.ffbbLinked = linked.length;
  }
  if (role === 'coach' && team) {
    const linked = await ffbbLinkedTeams([team.id]).catch(() => null);
    if (linked) info.teamLinked = linked.includes(team.id);
  }
  if (role === 'coach' || role === 'joueur' || role === 'parent') {
    const list = items ?? (await loadAgenda(c, 8).catch(() => null))?.items ?? [];
    const today = localDate(new Date());
    const mine = role === 'coach' ? list.filter(it => c.coachOf.has(it.ev.team_id)) : list.filter(it => it.mine.length);
    const tr = mine.find(it => it.ev.kind === 'training');
    if (tr) info.training = { today: tr.occ.date === today, time: time(tr.occ.start), title: tr.ev.title,
      when: tr.occ.date === today ? 'Ce soir' : parseLocalDate(tr.occ.date).toLocaleDateString('fr-FR', { weekday: 'long' }).replace(/^./, x => x.toUpperCase()) };
    const m = mine.find(it => it.ev.kind === 'match');
    if (m?.match) {
      const d = parseLocalDate(m.occ.date);
      info.match = { day: d.toLocaleDateString('fr-FR', { weekday: 'long' }),
        label: `${m.label} ${m.match.is_home === false ? 'à' : '–'} ${m.match.opponent} · ${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}` };
    }
  }
  return info;
}

/** La liste des étapes (bloc de l'accueil, Aide › Mes premiers pas). Les facultatives (D-122) : seulement dans l'aide,
 *  sous « Quand tu veux ». */
export function stepsBlock(role: GuideRole, info: StepInfo, opts: { later: boolean; optional?: boolean }): Html {
  const all = firstSteps(role, info);
  const steps = all.filter(s => !s.optional);
  const extra = opts.optional ? all.filter(s => s.optional) : [];
  const head = firstStepsHead(role, info);
  const { done, total } = stepProgress(steps);
  const now = nextStep(steps);
  const row = (s: FirstStep) => html`
    <li class="${s.done ? 'done' : ''} ${s === now ? 'gd-now' : ''}"><span class="mt-dot" aria-hidden="true">${s.done ? '✓' : ''}</span>
      <span class="mt-txt"><strong>${s.title}</strong><span>${s.sub}</span></span>
      ${!s.done && s.href ? html`<a class="btn ${s === now ? 'btn-primary' : 'btn-ghost'} btn-sm gd-show" href="${s.href}" data-guide="pp-${s.key}">${s.cta ?? 'Ouvrir'}</a>`
        : !s.done && s.tour ? html`<button type="button" class="btn ${s === now ? 'btn-primary' : 'btn-ghost'} btn-sm gd-show" data-tour="${s.tour}" data-step="${s.key}">${s.cta ?? 'Montre-moi'}</button>` : ''}</li>`;
  return html`
    <section class="block mt-todo gd-pp" data-guide="first-steps" aria-label="${head.title}">
      <div class="block-head"><h3>${head.title}</h3><span class="mt-count">${done} / ${total}</span></div>
      ${head.sub ? html`<p class="gd-pp-sub">${head.sub}</p>` : ''}
      <div class="mt-bar" aria-hidden="true"><span style="width:${Math.round((100 * done) / Math.max(1, total))}%"></span></div>
      <ul class="mt-list">${steps.map(row)}</ul>
      ${extra.length ? html`<p class="gd-pp-sub">Quand tu veux</p><ul class="mt-list">${extra.map(row)}</ul>` : ''}
      ${opts.later ? html`<p class="gd-later">Ce bloc disparaît quand tout est fait · toujours dans Aide › Mes premiers pas</p>` : ''}
    </section>`;
}

/**
 * Le bloc de l'accueil : rien en démo, rien quand tout est fait. Rend le nombre d'étapes qui restent
 * (le bureau adapte sa phrase d'accueil), ou null.
 */
export async function renderFirstSteps(el: HTMLElement, c: Ctx, opts: { items?: AgendaItem[]; ask?: string | null } = {}): Promise<number | null> {
  noteStandalone(c);
  const role = guideRoleOf(c);
  if (!role || !STEP_KEYS[role].length || await demoNow()) return null;
  const state = await loadState(c);
  const steps = stepsOfRole(state, role);
  if (!steps || (state?.demo && !forcedReal())) return null;
  const left = steps.filter(s => !s.done).length;
  if (!left) { mount(el, html``); return 0; }
  const info = await infoFor(c, role, steps, opts.items, optionalOf(state, role));
  if (opts.ask !== undefined) info.ask = opts.ask;
  mount(el, stepsBlock(role, info, { later: true }));
  return left;
}

/** Aide : le chemin du rôle affiché (maquette c3) ; rien quand tout est fait. */
export async function renderPathCard(el: HTMLElement, c: Ctx): Promise<void> {
  const state = await loadState(c);
  if (!state || (state.demo && !forcedReal()) || await demoNow()) return;
  const prefer = guideRoleOf(c);
  const roles = [...state.roles].filter(r => STEP_KEYS[r.role]?.length).sort((a, b) => (a.role === prefer ? -1 : b.role === prefer ? 1 : 0));
  const r = roles.find(x => x.steps.some(s => !s.done));
  if (!r) return;
  const info = await infoFor(c, r.role, r.steps, undefined, r.optional ?? []);
  const steps = firstSteps(r.role, info);
  const nx = nextStep(steps)!;
  const prog = stepProgress(steps);
  const self = c.people.find(p => p.user_id === c.uid);
  const label = { bureau: 'bureau', coach: 'coach', joueur: agree(self ? genderOf(c, self.id) : 'X', 'joueur', 'joueuse'), parent: 'parent', partenaire: 'partenaire', technique: 'resp. technique' }[r.role];
  mount(el, html`
    <section class="block gd-path" data-guide="help-path">
      <div class="block-head"><h3>Mes premiers pas · ${label}</h3><span class="mt-count">${prog.done} / ${prog.total}</span></div>
      <div class="gd-path-bar" style="--n:${prog.total}" aria-hidden="true">${prog.counted.map(s => html`<i class="${s.done ? 'on' : ''}"></i>`)}</div>
      <div class="gd-path-next"><span class="todo-main"><strong>Prochaine étape : ${nx.title.charAt(0).toLowerCase()}${nx.title.slice(1)}</strong><small>${nx.sub}</small></span>
        ${nx.href ? html`<a class="btn btn-primary btn-sm gd-show" href="${nx.href}">${nx.cta ?? 'Ouvrir'}</a>`
          : nx.tour ? html`<button type="button" class="btn btn-primary btn-sm gd-show" data-tour="${nx.tour}">${nx.cta ?? 'Montre-moi'}</button>` : ''}</div>
      <a class="gd-path-all" href="#/aide/premiers-pas">Toutes les étapes ›</a>
    </section>`);
}

/** Aide › Mes premiers pas : chaque rôle, même fini. */
export async function renderAllFirstSteps(el: HTMLElement, c: Ctx): Promise<void> {
  const state = await loadState(c, true);
  const roles = (state?.roles ?? []).filter(r => STEP_KEYS[r.role]?.length);
  if (!roles.length) {
    mount(el, html`<div class="empty"><p class="empty-title">Rien à régler ici.</p><p>${personaOf(c) === 'partenaire' ? 'Vos' : 'Tes'} premiers pas apparaissent avec un rôle au club : bureau, coach, joueuse ou joueur, parent, partenaire.</p></div>`);
    return;
  }
  const blocks = await Promise.all(roles.map(async r => stepsBlock(r.role, await infoFor(c, r.role, r.steps, undefined, r.optional ?? []), { later: false, optional: true })));
  mount(el, html`${blocks}`);
}
